import { Dispatch, FC, useMemo } from 'react';
import { AnyAction } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { PlannerSecondaryPanelType } from 'store/dealManagement/types';
import { changeNewProductField, clearItemInNewProduct, hideSecondaryPanel } from 'store/productManagement/reducer';
import { useOnChangePanelVisibility } from 'components/pages/PCM/hooks/useOnChangePanelVisibility';
import poisSvg from 'assets/icons/pois.svg';
import SectionButton, { SectionButtonTheme } from 'lib/SectionButton';
import { SectionSelectedType } from 'lib/SectionButton/SectionButton.types';
import { Store } from 'components/common/types/Store.types';
import { ProductFieldName, SelectionPermission } from 'store/productManagement/reducer.types';
import { countIncludedAndExcludedPOI, groupPOIByParentCategory } from 'utils/poiUtils';
import { POIChipList } from 'components/pages/Planner/PlannerSections/DataTargetingContent/PointsOfInterest/POIChipList';
import { POI } from 'components/common/types/Planner.types';
import { getIsReadOnly } from 'store/productManagement/selectors';
import { ProductPoiPanel } from './ProductPoiPanel';
import { isValueRequired } from '../../utils/isValueRequired';
import {
  getNoRestrictionsDisplayValue,
  getValueRequiredPermissionDisplayValue,
} from '../../utils/getNoRestrictionsDisplayValue';
import { PERMISSION_LABELS } from '../../consts';

const getTheme = (selectedPermission?: SelectionPermission): SectionButtonTheme => {
  return selectedPermission ? SectionButtonTheme.SOLID : SectionButtonTheme.DASHED;
};

const getSelectedProp = (
  selectedPOIs: POI[],
  selectedPermission?: SelectionPermission,
): SectionSelectedType | undefined => {
  if (selectedPOIs.length > 0) {
    return undefined;
  }

  if (selectedPermission && isValueRequired(selectedPermission)) {
    return getValueRequiredPermissionDisplayValue(selectedPermission);
  }

  return getNoRestrictionsDisplayValue(selectedPermission);
};

const handlePoiChange = ({
  selectedPOIs,
  poiToRemove,
  included,
  selectedPermission,
  dispatch,
  fieldName,
}: {
  selectedPOIs: POI[];
  poiToRemove: string[];
  included?: boolean;
  selectedPermission: SelectionPermission;
  dispatch: Dispatch<AnyAction>;
  fieldName: ProductFieldName;
}): void => {
  const poiSet = new Set(poiToRemove);
  const newSelectedPOIs = selectedPOIs.filter((value) => {
    return (
      (!poiSet.has(value.code) && !poiSet.has(value.name)) || (included !== undefined && value.include !== included)
    );
  });

  if (newSelectedPOIs.length) {
    dispatch(
      changeNewProductField({
        fieldName,
        value: newSelectedPOIs,
        selectedPermission: { code: selectedPermission, name: PERMISSION_LABELS[selectedPermission] },
      }),
    );
  } else {
    dispatch(clearItemInNewProduct({ fieldName }));
  }
};

const ProductPoi: FC = () => {
  const dispatch = useDispatch();
  const isReadOnly = useSelector(getIsReadOnly);
  const mediaType = useSelector((state: Store) => state.productManagement.newProduct.mediaType?.value);
  const channels = useSelector((state: Store) => state.productManagement.newProduct.channels?.value);
  const secondaryPanelType = useSelector((state: Store) => state.productManagement.secondaryPanel);
  const selectedPOIs = useSelector((state: Store) => state.productManagement.newProduct.poi?.value) ?? [];
  const selectedPermission = useSelector((state: Store) => state.productManagement.newProduct.poi?.selectionPermission);

  const onChangePanelVisibility = useOnChangePanelVisibility();
  const fieldName = ProductFieldName.POI;
  const isPoiPanelOpen = secondaryPanelType === PlannerSecondaryPanelType.POI;

  const { included: collapsedIncludedPoiNumber, excluded: collapsedExcludedPoiNumber } = useMemo(
    () => countIncludedAndExcludedPOI(selectedPOIs),
    [selectedPOIs],
  );

  const selectedPOIByParentCategory = useMemo(() => groupPOIByParentCategory(selectedPOIs), [selectedPOIs]);

  const onRemovePoiChip = (poiToRemove?: string[], included?: boolean): void => {
    if (!selectedPermission) return;

    if (!poiToRemove) {
      if (included !== undefined) {
        const newSelectedPOIs = selectedPOIs.filter((value) => value.include !== included);
        if (!newSelectedPOIs.length) {
          dispatch(clearItemInNewProduct({ fieldName }));
          return;
        }

        dispatch(
          changeNewProductField({
            fieldName,
            value: newSelectedPOIs,
            selectedPermission: { code: selectedPermission, name: PERMISSION_LABELS[selectedPermission] },
          }),
        );
        return;
      }

      dispatch(clearItemInNewProduct({ fieldName }));
      return;
    }

    if (!poiToRemove.length) return;

    handlePoiChange({ selectedPOIs, poiToRemove, included, selectedPermission, dispatch, fieldName });
  };

  const isDisabled = !mediaType || !channels?.length;

  return (
    <>
      <SectionButton
        dataTestId="product-button-poi"
        label="POI"
        icon={poisSvg}
        isDisabled={isDisabled}
        isEllipsisDisabled={isDisabled || isReadOnly}
        theme={getTheme(selectedPermission)}
        isActive={isPoiPanelOpen}
        selected={getSelectedProp(selectedPOIs, selectedPermission)}
        onClick={() => onChangePanelVisibility(PlannerSecondaryPanelType.POI)}
        onClear={() => {
          dispatch(clearItemInNewProduct({ fieldName }));
          dispatch(hideSecondaryPanel());
        }}
        onRemoveChip={onRemovePoiChip}
        isShowLessToggleDisabled={selectedPOIs.length <= 1}
        collapsedExcludedNumber={collapsedExcludedPoiNumber}
        collapsedIncludedNumber={collapsedIncludedPoiNumber}
      >
        {selectedPOIs.length > 0 && (
          <POIChipList
            selectedPOIByParentCategory={selectedPOIByParentCategory}
            onRemovePoiChip={onRemovePoiChip}
            isDisabled={isDisabled || isReadOnly}
          />
        )}
      </SectionButton>
      {isPoiPanelOpen && <ProductPoiPanel onCancel={() => dispatch(hideSecondaryPanel())} isReadOnly={isReadOnly} />}
    </>
  );
};

export default ProductPoi;
